/* eslint-disable @typescript-eslint/no-explicit-any */
// token-service.ts
// Service for handling token operations (e.g., get, set, remove) in localStorage/sessionStorage

import { useAuthStore } from '../../store/useAuthStore';
import { ROUTES } from '../../utils/pathRoutes';
import { buildLogoutHref, notifyLogoutAcrossTabs } from '@/utils/authCrossTabSync';
import { parseJwtPayload } from '@/utils/jwtClaims';
import { SweetAlertService } from '../alert/sweetAlertService';
import { authApi } from '../authen';
import requestManager from '../request-manager';

export interface TokenValidationResult {
    isValid: boolean;
    token?: string;
    error?: string;
}

class TokenService {
    private static instance: TokenService;
    private isRefreshing = false;
    private refreshPromise: Promise<boolean> | null = null;
    private clearSessionRedirectTimeout: ReturnType<typeof setTimeout> | null = null;
    private isHandlingTokenExpired = false;

    private constructor() { }

    public static getInstance(): TokenService {
        if (!TokenService.instance) {
            TokenService.instance = new TokenService();
        }
        return TokenService.instance;
    }

    /**
     * Kiểm tra token có hết hạn không
     * @param token - JWT token cần kiểm tra
     * @returns true nếu token đã hết hạn, false nếu còn hạn
     */
    public isTokenExpired(token: string): boolean {
        try {
            // LoginEncrypt=true + thiếu enc=1 → parseJwtPayload trả null → coi hết hạn
            const decoded = parseJwtPayload(token);
            if (!decoded?.exp) return true;
            const now = Math.floor(Date.now() / 1000);
            return decoded.exp <= now + 30;
        } catch (error) {
            console.error('❌ Error decoding token:', error);
            return true;
        }
    }

    /**
     * Lấy thời gian hết hạn của token (timestamp)
     * @param token - JWT token
     * @returns timestamp hoặc null nếu không decode được
     */
    public getTokenExpiration(token: string): number | null {
        try {
            const decoded = JSON.parse(atob(token.split('.')[1]));
            return decoded.exp || null;
        } catch (error) {
            console.error('❌ Error decoding token expiration:', error);
            return null;
        }
    }

    /**
     * Làm mới access token
     * @returns Promise<boolean> - true nếu refresh thành công, false nếu thất bại
     */
    public async refreshAccessToken(): Promise<boolean> {
        // Tránh multiple refresh calls cùng lúc
        if (this.isRefreshing && this.refreshPromise) {
            return await this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = this._performRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    /**
     * Thực hiện refresh token
     */
    private async _performRefresh(): Promise<boolean> {
        try {
            const refeshToken = useAuthStore.getState().refreshToken;
            const newTokenResponse: any = await authApi.refeshToken(String(refeshToken || ''));

            const response = newTokenResponse;
            if (response?.success == false) {
                console.warn('❌ Token refresh failed - server returned success: false');
                await this.handleTokenExpired();
                return false;
            } else {
                if (response?.success === true) {
                    useAuthStore.getState().setAccessToken(response.accessToken);
                    useAuthStore.getState().setRefreshToken(response.refreshToken);
                    return true;
                } else {
                    console.warn('❌ Token refresh failed - server returned success: false');
                    await this.handleTokenExpired();
                    return false;
                }
            }
        } catch (error) {
            console.error('Failed to refresh token', {
                message: error instanceof Error ? error.message : String(error),
            });
            await this.handleTokenExpired();
            return false;
        }
    }

    /**
     * Ẩn toàn bộ layout app trước khi hiện cảnh báo hết phiên,
     * tránh còn hiện component/broken UI phía sau modal.
     */
    private blankOutAppShell(): void {
        if (typeof document === 'undefined') return;

        const appRoot = document.getElementById('__next');
        if (appRoot) {
            appRoot.setAttribute('aria-hidden', 'true');
            appRoot.style.visibility = 'hidden';
            appRoot.style.pointerEvents = 'none';
        }

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.background = '#f5f5f5';
    }

    /**
     * Xử lý khi token hết hạn hoặc không thể refresh
     */
    public async handleTokenExpired(): Promise<void> {
        if (this.isHandlingTokenExpired) return;
        this.isHandlingTokenExpired = true;

        const returnUrl = document.location.pathname;
        this.blankOutAppShell();

        await SweetAlertService.warning(
            'Hệ thống sẽ chuyển về trang đăng nhập để bạn có thể đăng nhập lại.',
            'Phiên đăng nhập đã hết hạn',
            async () => {
                await this.clearSessionAndRedirect(returnUrl);
            },
            true
        );
    }

    /**
     * Kiểm tra và refresh token nếu cần
     * @param token - Token hiện tại (optional, sẽ lấy từ store nếu không truyền)
     * @returns Promise<TokenValidationResult>
     */
    public async validateAndRefreshToken(token?: string): Promise<TokenValidationResult> {
        const authToken = token || useAuthStore.getState().accessToken;

        if (!authToken) {
            console.warn('⚠️ No auth token found');
            await this.handleTokenExpired();
            return {
                isValid: false,
                error: 'No auth token found',
            };
        }

        if (this.isTokenExpired(authToken)) {
            const refreshSuccess = await this.refreshAccessToken();

            if (refreshSuccess) {
                const newToken = useAuthStore.getState().accessToken;
                return {
                    isValid: true,
                    token: newToken || undefined,
                };
            } else {
                return {
                    isValid: false,
                    error: 'Failed to refresh token',
                };
            }
        }

        return {
            isValid: true,
            token: authToken,
        };
    }

    /**
     * Lấy token hợp lệ (refresh nếu cần)
     * @returns Promise<string | null> - Token hợp lệ hoặc null nếu không thể lấy được
     */
    public async getValidToken(): Promise<string | null> {
        const result = await this.validateAndRefreshToken();
        return result.isValid ? result.token || null : null;
    }

    /**
     * Kiểm tra xem có đang trong quá trình refresh không
     */
    public isCurrentlyRefreshing(): boolean {
        return this.isRefreshing;
    }

    /**
     * Clear toàn bộ session và redirect về login
     */
    public async clearSessionAndRedirect(returnUrl: string | null): Promise<void> {
        // Hủy API đang pending trước khi clear session — tránh alert lỗi sau logout
        requestManager.cancelAll();
        notifyLogoutAcrossTabs(returnUrl);
        await useAuthStore.getState().clearAuthAll();
        setTimeout(() => {
            if (returnUrl?.includes(ROUTES.LOGOUT)) {
                returnUrl = '/';
            }
            // if (returnUrl && returnUrl != '/' && returnUrl != '') {
            //     window.location.href = `${ROUTES.LOGOUT}/${(returnUrl == null ? '' : `?return-url=${returnUrl}`)}`;
            // } else {
            //     window.location.href = ROUTES.LOGOUT;
            // }
            window.location.href = buildLogoutHref(returnUrl);
        }, 500);
    }

    /**
     * Decode JWT payload (không verify signature)
     * @param token - JWT token
     * @returns Payload object hoặc null nếu không decode được
     */
    public decodeTokenPayload(token: string): any | null {
        return parseJwtPayload(token);
    }

    /**
     * Lấy thông tin user từ token
     * @param token - JWT token (optional, sẽ lấy từ store nếu không truyền)
     * @returns User info object hoặc null
     */
    public getUserFromToken(token?: string): any | null {
        const authToken = token || useAuthStore.getState().accessToken;
        if (!authToken) return null;

        const payload = this.decodeTokenPayload(authToken);
        if (!payload) return null;

        const nameId =
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
            || payload.nameid
            || payload.sub
            || payload.userId
            || payload.id;
        const email =
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress']
            || payload.email;
        const name =
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
            || payload.unique_name
            || payload.username
            || payload.name;
        const role =
            payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
            || payload.roles
            || payload.role;

        return {
            id: nameId,
            email,
            username: name,
            roles: role,
            tenantId: payload.TenantID,
            departmentId: payload.IdDepartment,
            exp: payload.exp,
            iat: payload.iat,
        };
    }
}

export const tokenService = TokenService.getInstance();
